import { getText } from 'i18n/intl';
import type { CategoryColor } from '@timeedit/calendar-integration-api-contract';
import { TEInput, Typography } from '@timeedit/ui-components';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import { intlPrefix } from './MS365SyncSettingsDetails';

type CategoryColorSelectorProps = {
  value: CategoryColor | null | undefined;
  onChange: (value: CategoryColor) => void;
};
export const CategoryColorSelector = ({
  value,
  onChange,
}: CategoryColorSelectorProps) => {
  return (
    <TEInput.Select
      label={getText(`${intlPrefix}.category_color.label`)}
      tooltip={getText(`${intlPrefix}.category_color.tooltip`)}
      options={colorOptions}
      optionRender={renderColorOption}
      labelRender={renderColorOption}
      value={value}
      onChange={onChange}
    />
  );
};

const MS_PRESET_COLORS: Record<
  string,
  { colorName: string; colorHex: string }
> = {
  none: { colorName: 'None', colorHex: '' },
  preset0: { colorName: 'Red', colorHex: '#FF0000' },
  preset1: { colorName: 'Orange', colorHex: '#FFA500' },
  preset2: { colorName: 'Brown', colorHex: '#A52A2A' },
  preset3: { colorName: 'Yellow', colorHex: '#FFFF00' },
  preset4: { colorName: 'Green', colorHex: '#008000' },
  preset5: { colorName: 'Teal', colorHex: '#008080' },
  preset6: { colorName: 'Olive', colorHex: '#808000' },
  preset7: { colorName: 'Blue', colorHex: '#0000FF' },
  preset8: { colorName: 'Purple', colorHex: '#800080' },
  preset9: { colorName: 'Cranberry', colorHex: '#DC143C' },
  preset10: { colorName: 'Steel', colorHex: '#95A5A6' },
  preset11: { colorName: 'DarkSteel', colorHex: '#2F4F4F' },
  preset12: { colorName: 'Gray', colorHex: '#808080' },
  preset13: { colorName: 'DarkGray', colorHex: '#A9A9A9' },
  preset14: { colorName: 'Black', colorHex: '#000000' },
  preset15: { colorName: 'DarkRed', colorHex: '#8B0000' },
  preset16: { colorName: 'DarkOrange', colorHex: '#FF8C00' },
  preset17: { colorName: 'DarkBrown', colorHex: '#5C4033' },
  preset18: { colorName: 'DarkYellow', colorHex: '#CCCC00' },
  preset19: { colorName: 'DarkGreen', colorHex: '#006400' },
  preset20: { colorName: 'DarkTeal', colorHex: '#004C4C' },
  preset21: { colorName: 'DarkOlive', colorHex: '#556B2F' },
  preset22: { colorName: 'DarkBlue', colorHex: '#00008B' },
  preset23: { colorName: 'DarkPurple', colorHex: '#4B0082' },
  preset24: { colorName: 'DarkCranberry', colorHex: '#990000' },
};

const colorOptions = Object.entries(MS_PRESET_COLORS).map(([key, value]) => ({
  label: value.colorName,
  value: key,
}));

const renderColorOption = (option: DefaultOptionType) => {
  const color = MS_PRESET_COLORS[option.value as string];
  const colorHex = color?.colorHex;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {colorHex ? (
        <div
          style={{
            width: 16,
            height: 16,
            backgroundColor: colorHex,
            outline: '1px solid rgba(0, 0, 0, 0.12)',
            outlineOffset: -1,
            borderRadius: 6,
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: 16,
            height: 16,
            border: '1px dashed rgba(0, 0, 0, 0.25)',
            borderRadius: 6,
            flexShrink: 0,
          }}
        />
      )}
      <Typography.Text>{option.label}</Typography.Text>
    </div>
  );
};
